import { useEffect, useState } from "react";
import { Button } from "@/components/ui/shadcn/button";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/shadcn/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/shadcn/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/shadcn/alert-dialog";
import { quotesAPI } from "@/services/api";
import { AcceptedQuote, QuoteSide, QuoteUnit } from "@/types/quotes";
import {
  CheckIcon,
  ClipboardIcon,
  XCircleIcon,
  ChevronDownIcon,
} from "lucide-react";
import { DateTime } from "luxon";
import { formatAssetAmount, getFormattedTimeUntil } from "@/lib/utils";
import AddTradeExecutionDialog from "./components/AddTradeExecutionDialog";
import { useToast } from "@/hooks/use-toast";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";
import { truncateAddress } from "@/utils/crypto-address";
import { DateEnhancedAcceptedQuote } from "@/types/quotes";
import {
  SortableTableHeader,
  useTableSorting,
} from "@/components/dashboard/data-table/SortableTableHeader";
import { useColumnVisibility } from "@/components/dashboard/data-table/ColumnVisibility";
import { BaseDataTable } from "@/components/dashboard/data-table/BaseDataTable";
import { AcceptedQuoteStatus } from "@/types/quotes";

// Define the sortable keys type
type SortableKey =
  | keyof Omit<AcceptedQuote, "client">
  | "client_name"
  | "asset_pair"
  | "accepted_at_date"
  | "status";

// Define column visibility configuration
const initialColumnVisibility = {
  client_name: { label: "Client", visible: true },
  asset_pair: { label: "Asset Pair", visible: true },
  from: { label: "From", visible: true },
  to: { label: "To", visible: true },
  side: { label: "Side", visible: true },
  price: { label: "Price", visible: true },
  fees_bps: { label: "Fees (bps)", visible: true },
  status: { label: "Status", visible: true },
  accepted_at_date: { label: "Accepted At", visible: true },
};

export function AcceptedQuotesTab({
  handleExportCSV,
}: {
  handleExportCSV: () => void;
}) {
  const { toast } = useToast();
  const [quotes, setQuotes] = useState<DateEnhancedAcceptedQuote[]>([]);
  const [filteredQuotes, setFilteredQuotes] = useState<
    DateEnhancedAcceptedQuote[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedVmarketIds, setSelectedVmarketIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<AcceptedQuoteStatus[]>([AcceptedQuoteStatus.ACTIVE]);
  const [selectedQuote, setSelectedQuote] = useState<AcceptedQuote | null>(
    null
  );
  const [isExecuteDialogOpen, setIsExecuteDialogOpen] = useState(false);
  const [cryptoAddress, setCopiedAddress] = useState<string | null>(null);
  const [copiedURL, setCopiedURL] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [quoteToCancel, setQuoteToCancel] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Use the new sorting hook
  const { sortConfig, handleSort } = useTableSorting<SortableKey>(
    "accepted_at_date",
    "desc"
  );

  // Use the column visibility hook with a storage key
  const { columns, setColumns, isColumnVisible } = useColumnVisibility(
    initialColumnVisibility,
    "accepted-quotes-column-visibility"
  );

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedVmarketIds, selectedStatuses]);

  const fetchQuotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await quotesAPI.getAcceptedQuotes({
        page,
        size,
        vmarket_ids: selectedVmarketIds.length > 0 ? selectedVmarketIds : undefined,
        status: selectedStatuses,
        start_date: startDate ? startDate.toISOString() : undefined,
        end_date: endDate ? endDate.toISOString() : undefined,
      });

      // Enhance quotes with Date objects and numeric values for easier sorting
      const enhancedQuotes = response.items.map((quote) => ({
        ...quote,
        accepted_at_date: DateTime.fromISO(quote.accepted_at).toJSDate(),
        total_fiat_numeric: parseFloat(quote.total_to),
        total_crypto_numeric: parseFloat(quote.total_from),
      }));

      setQuotes(enhancedQuotes);
      setFilteredQuotes(enhancedQuotes);
      setTotal(response.total);
      setPages(response.pages);

      if (import.meta.env.DEV) {
        console.log("Accepted quotes fetched successfully:", {
          count: response.items.length,
          total: response.total,
          page,
          size,
          pages: response.pages,
          selectedVmarketIds,
          selectedStatuses,
          startDate,
          endDate,
        });
      }
    } catch (err) {
      handleApiError("Fetch Accepted Quotes", err, toast);
      setError("Failed to fetch accepted quotes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [page, size, selectedVmarketIds, selectedStatuses, startDate, endDate]);

  const handleExecuteClick = (quote: AcceptedQuote) => {
    setSelectedQuote(quote);
    setIsExecuteDialogOpen(true);
  };

  const handleCopy = (address: string) => {
    navigator.clipboard
      .writeText(address)
      .then(() => {
        setCopiedAddress(address);
        showSuccessToast(toast, "Copied", "Address copied to clipboard");
        setTimeout(() => setCopiedAddress(null), 2000);
      })
      .catch((error) => {
        console.error("Failed to copy address:", error);
        toast({
          title: "Error",
          description: "Failed to copy address to clipboard",
          variant: "error",
        });
      });
  };

  const publicHttpUrl =
    import.meta.env.VITE_PUBLIC_HTTP_URL || "http://localhost:5173";

  const handleCopyToClipboard = (quoteUrl: string) => {
    navigator.clipboard
      .writeText(quoteUrl)
      .then(() => {
        setCopiedURL(quoteUrl);
        showSuccessToast(toast, "Copied", "Quote link copied to clipboard");
        setTimeout(() => setCopiedURL(null), 2000);
      })
      .catch((error) => {
        console.error("Failed to copy link:", error);
        toast({
          title: "Error",
          description: "Failed to copy link to clipboard",
          variant: "error",
        });
      });
  };

  const handleCancelAcceptedQuote = async (slug: string) => {
    try {
      await quotesAPI.cancelAcceptedQuote(slug);
      showSuccessToast(
        toast,
        "Success",
        "Accepted quote cancelled successfully"
      );
      // Refresh the quotes list
      fetchQuotes();
    } catch (err) {
      handleApiError("Cancel Accepted Quote", err, toast);
    }
  };

  const toggleRow = (slug: string) => {
    setExpandedRow(expandedRow === slug ? null : slug);
  };

  const renderTableHeader = ({
    sortConfig,
    handleSort,
    isColumnVisible,
  }: {
    sortConfig: { key: string; direction: "asc" | "desc" | null };
    handleSort: (key: string) => void;
    isColumnVisible: (key: string) => boolean;
  }) => (
    <>
      {isColumnVisible("client_name") && (
        <TableHead>
          <SortableTableHeader
            label="Client"
            sortKey="client_name"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("asset_pair") && (
        <TableHead>
          <SortableTableHeader
            label="Asset Pair"
            sortKey="asset_pair"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("side") && (
        <TableHead>
          <SortableTableHeader
            label="Side"
            sortKey="side"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("from") && <TableHead>From</TableHead>}
      {isColumnVisible("to") && <TableHead>To</TableHead>}
      {isColumnVisible("price") && <TableHead>Price</TableHead>}
      {isColumnVisible("fees_bps") && (
        <TableHead>
          <SortableTableHeader
            label="Fees (bps)"
            sortKey="fees_bps"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      {isColumnVisible("status") && <TableHead>Status</TableHead>}
      {isColumnVisible("accepted_at_date") && (
        <TableHead>
          <SortableTableHeader
            label="Accepted At"
            sortKey="accepted_at_date"
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </TableHead>
      )}
      <TableHead className="w-0"></TableHead>
    </>
  );

  const renderTableRow = (quote: DateEnhancedAcceptedQuote) => {
    const accepted_at = DateTime.fromISO(quote.accepted_at, {
      zone: "utc",
    });
    const now = DateTime.utc();
    const timeSince = getFormattedTimeUntil(now, accepted_at);
    const quoteUrl = `${publicHttpUrl}/client/accept-quote/${quote.slug}`;
    const isExpanded = expandedRow === quote.slug;

    const isBuy = quote.side === QuoteSide.BUY;

    // For BUY:
    //   - total_from is always in counter (MXN)
    //   - total_to is always in base (BTC)
    // For SELL:
    //   - total_from is always in base (BTC)
    //   - total_to is always in counter (MXN)
    const fromAmount = isBuy
      ? quote.total_from // BUY: Give counter (MXN)
      : quote.total_from; // SELL: Give base (BTC)

    const toAmount = isBuy
      ? quote.total_to // BUY: Get base (BTC)
      : quote.total_to; // SELL: Get counter (MXN)

    // For BUY: fromAsset is counter (MXN), toAsset is base (BTC)
    // For SELL: fromAsset is base (BTC), toAsset is counter (MXN)
    const fromAsset = isBuy ? quote.vmarket.counter : quote.vmarket.base;
    const toAsset = isBuy ? quote.vmarket.base : quote.vmarket.counter;

    // const url = "https://otc.bax.mx/client/accept-quote/ETH-USD-250501-065216-59d31503"
    // const truncatedUrl = truncateAddress(url, 19, 8)
    // // const url2xTruncated = truncateAddress(truncatedUrl, 31, 9)
    // const url2xTruncated = truncatedUrl
    // console.log(truncatedUrl)
    // console.log(url2xTruncated)

    return (
      <>
        <TableRow
          key={quote.slug}
          className={`hover:bg-gray-100 transition cursor-pointer ${
            isExpanded ? "bg-gray-100" : ""
          }`}
          onClick={() => toggleRow(quote.slug)}
        >
          {isColumnVisible("client_name") && (
            <TableCell>
              <span className="cursor-pointer underline decoration-dotted">
                {quote.client.name}
              </span>
            </TableCell>
          )}
          {isColumnVisible("asset_pair") && (
            <TableCell>
              {`${quote.vmarket.base.code}/${quote.vmarket.counter.code}`}
            </TableCell>
          )}
          {isColumnVisible("side") && (
            <TableCell>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  quote.side === QuoteSide.BUY
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {quote.side}
              </span>
            </TableCell>
          )}
          {isColumnVisible("from") && (
            <TableCell>
              {`${formatAssetAmount(fromAmount, fromAsset)} ${fromAsset.code}`}
            </TableCell>
          )}
          {isColumnVisible("to") && (
            <TableCell>
              {`${formatAssetAmount(toAmount, toAsset)} ${toAsset.code}`}
            </TableCell>
          )}
          {isColumnVisible("price") && (
            <TableCell>
              {`${quote.vmarket.counter.code} ${formatAssetAmount(
                quote.price,
                quote.vmarket.counter
              )}`}
            </TableCell>
          )}
          {isColumnVisible("fees_bps") && (
            <TableCell>{quote.fees_bps}</TableCell>
          )}
          {isColumnVisible("status") && (
            <TableCell>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  quote.status === AcceptedQuoteStatus.ACTIVE
                    ? "bg-green-100 text-green-700"
                    : quote.status === AcceptedQuoteStatus.CANCELLED
                    ? "bg-red-100 text-red-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {quote.status}
              </span>
            </TableCell>
          )}
          {isColumnVisible("accepted_at_date") && (
            <TableCell>
              {timeSince.includes("-") ? (
                <span className="text-red-700">
                  {timeSince.replace("-", "")} ago
                </span>
              ) : (
                <span>{timeSince}</span>
              )}
            </TableCell>
          )}
          <TableCell className="w-0">
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${
                isExpanded ? "transform rotate-180" : ""
              }`}
            />
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow className="bg-gray-50">
            <TableCell colSpan={11}>
              <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                {/* Header with Status Badge */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Quote Details</h3>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      quote.status === AcceptedQuoteStatus.ACTIVE
                        ? "bg-green-100 text-green-700"
                        : quote.status === AcceptedQuoteStatus.CANCELLED
                        ? "bg-red-100 text-red-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {quote.status}
                  </span>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column - Information Cards Side by Side */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Client Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Client Information</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">Name:</span>
                          <span className="font-medium text-gray-900">{quote.client.name}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-14">Email:</span>
                          <span className="font-medium text-gray-900">{quote.client.email}</span>
                        </div>
                        {quote.client.phone && (
                          <div className="flex items-center">
                            <span className="text-gray-500 w-14">Phone:</span>
                            <span className="font-medium text-gray-900">{quote.client.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Accepted Quote Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Accepted Quote Details </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">Asset Pair:</span>
                          <span className="font-medium text-gray-900">
                            {quote.vmarket.base.code}/{quote.vmarket.counter.code}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">Amount:</span>
                          <span className="font-medium text-gray-900">
                            {formatAssetAmount(
                              quote.amount.toString(),
                              quote.unit === QuoteUnit.BASE ? quote.vmarket.base : quote.vmarket.counter
                            )}
                            {" "}
                            {quote.unit === QuoteUnit.BASE ? quote.vmarket.base.code : quote.vmarket.counter.code}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Side:
                          </span>
                          <span className="font-medium text-gray-900">
                            {quote.side.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            Price:
                          </span>
                          <span className="font-medium text-gray-900">
                            {`${quote.vmarket.counter.code} ${formatAssetAmount(
                              quote.price,
                              quote.vmarket.counter,
                            )}`}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            From:
                          </span>
                          <span className="font-medium text-gray-900">
                            {`${formatAssetAmount(fromAmount, fromAsset)} ${fromAsset.code}`}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 w-20">
                            To:
                          </span>
                          <span className="font-medium text-gray-900">
                            {`${formatAssetAmount(toAmount, toAsset)} ${toAsset.code}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - URL, Crypto Address and Actions */}
                  <div className="space-y-4">
                    {/* Quote URL Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Quote URL</h4>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg py-2 px-3 border border-gray-200">
                        <div className="flex-1 font-mono text-[0.9rem] text-gray-600 break-all mr-4 py-2 mt-1">
                          <a href={quoteUrl} target="_blank" rel="noopener noreferrer">{truncateAddress(quoteUrl, 19, 8)}</a>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyToClipboard(quoteUrl);
                              }}
                              className="text-[0.8rem] max-w-[100px] max-h-[10px] bg-white hover:bg-gray-50 p-4"
                            >
                              {copiedURL === quoteUrl ? (
                                <span className="flex items-center"><CheckIcon className="max-w-[12px] max-h-[12px] mr-2 text-green-500" /> Copied</span>
                              ) : (
                                <span className="flex items-center"><ClipboardIcon className="max-w-[12px] max-h-[12px] mr-2" /> Copy URL</span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copy quote URL to clipboard</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Crypto Address Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <h4 className="font-semibold text-gray-900">Crypto Address</h4>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-lg py-2 px-3 border border-gray-200">
                        {quote.crypto_address ? (
                          <>
                            <div className="flex-1 font-mono text-[0.9rem] text-gray-600 break-all mr-4 py-2 mt-1">
                              {quote.crypto_address}
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (quote.crypto_address) {
                                      handleCopy(quote.crypto_address);
                                    }
                                  }}
                                  className="text-[0.8rem] bg-white hover:bg-gray-50 px-3 max-h-8"
                                >
                                  {cryptoAddress === quote.crypto_address ? (
                                    <span className="flex items-center"><CheckIcon className="max-w-[12px] max-h-[12px] mr-2 text-green-500" />Copied</span>
                                  ) : (
                                    <span className="flex items-center"><ClipboardIcon className="max-w-[12px] max-h-[12px] mr-2" />Copy Address</span>
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Copy crypto address to clipboard</p>
                              </TooltipContent>
                            </Tooltip>
                          </>
                        ) : (
                          <span className="text-gray-400 text-sm italic">
                            Not provided
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions Section */}
                    {quote.status === AcceptedQuoteStatus.ACTIVE && (
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExecuteClick(quote);
                          }}
                          className="bg-green-100 text-green-800 hover:bg-green-200 border border-green-200"
                        >
                          Execute Trade
                        </Button>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuoteToCancel(quote.slug);
                              }}
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            >
                              <XCircleIcon className="h-4 w-4 mr-2" />
                              Cancel Quote
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Cancel this accepted quote</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <BaseDataTable
        title="Accepted Quotes"
        description="List of accepted quotes"
        data={quotes}
        filteredData={filteredQuotes}
        setFilteredData={setFilteredQuotes}
        loading={loading}
        error={error}
        onRetry={fetchQuotes}
        onExportCSV={handleExportCSV}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedVmarketIds={selectedVmarketIds}
        setSelectedVmarketIds={setSelectedVmarketIds}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        columns={columns}
        default_columns={initialColumnVisibility}
        storageKey="accepted-quotes-column-visibility"
        setColumns={setColumns}
        sortableKeys={[
          "client_name",
          "asset_pair",
          "accepted_at_date",
          "status",
        ]}
        defaultSortKey="accepted_at_date"
        defaultSortDirection="desc"
        renderTableHeader={renderTableHeader}
        renderTableRow={renderTableRow}
        withQuoteTypeBoxes={true}
        isAcceptedQuotes={true}
        pagination={{
          page,
          setPage,
          size,
          setSize,
          total,
          pages,
        }}
        onResetColumns={fetchQuotes}
      />

      {selectedQuote && (
        <AddTradeExecutionDialog
          quote={selectedQuote}
          isOpen={isExecuteDialogOpen}
          onOpenChange={setIsExecuteDialogOpen}
          onTradeExecuted={fetchQuotes}
        />
      )}

      <AlertDialog
        open={!!quoteToCancel}
        onOpenChange={(open) => !open && setQuoteToCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Accepted Quote</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this accepted quote? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => quoteToCancel && handleCancelAcceptedQuote(quoteToCancel)}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Cancel Quote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
